import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchPostList } from '../api/postApi';
import Pagination from '../components/Pagination';
import { RECEIPT_STATUS_OPTIONS, SEARCH_TYPE_OPTIONS } from '../constants/post';

const STATUS_LABEL_MAP = RECEIPT_STATUS_OPTIONS.reduce((acc, cur) => {
  acc[cur.value] = cur.label;
  return acc;
}, {});

const splitDateTimeDisplay = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return {
    date: date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }),
    time: date.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }),
  };
};

function DateTimeCell({ value }) {
  const parts = splitDateTimeDisplay(value);
  if (!parts) return '-';

  return (
    <span className="datetime-stack">
      <span className="datetime-date">{parts.date}</span>
      <span className="datetime-time">{parts.time}</span>
    </span>
  );
}

function PostListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const page = Number(searchParams.get('page') ?? 0);
  const size = Number(searchParams.get('size') ?? 10);

  const [filters, setFilters] = useState({
    keyword: searchParams.get('keyword') ?? '',
    searchType: searchParams.get('searchType') ?? '',
    receiptStatus: searchParams.get('receiptStatus') ?? '',
    startDate: searchParams.get('startDate') ?? '',
    endDate: searchParams.get('endDate') ?? '',
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const result = await fetchPostList({
          ...filters,
          page,
          size,
          sort: 'id,desc',
        });
        setData(result);
      } catch (error) {
        window.alert(`목록 조회 실패: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filters, page, size]);

  const onChangeFilter = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onSearch = (event) => {
    event.preventDefault();
    setSearchParams({
      ...filters,
      page: '0',
      size: String(size),
    });
  };

  const onMovePage = (nextPage) => {
    setSearchParams({
      ...filters,
      page: String(nextPage),
      size: String(size),
    });
  };

  const content = data?.content ?? [];

  return (
    <section className="page">
      <div className="page-header">
        <h2>AS 목록 조회</h2>
        <button onClick={() => navigate('/posts/new')}>작성 페이지</button>
      </div>

      <form className="filter-row" onSubmit={onSearch}>
        <select name="searchType" value={filters.searchType} onChange={onChangeFilter}>
          <option value="">검색 구분</option>
          {SEARCH_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <input name="keyword" placeholder="키워드" value={filters.keyword} onChange={onChangeFilter} />
        <select name="receiptStatus" value={filters.receiptStatus} onChange={onChangeFilter}>
          <option value="">전체 상태</option>
          {RECEIPT_STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <input type="date" name="startDate" value={filters.startDate} onChange={onChangeFilter} />
        <input type="date" name="endDate" value={filters.endDate} onChange={onChangeFilter} />
        <button type="submit">검색</button>
      </form>

      {loading ? (
        <p>불러오는 중...</p>
      ) : (
        <div className="table-wrapper fixed-list-wrapper">
          <table className="post-list-table">
            <thead>
              <tr>
                <th className="col-post-id">접수 순번</th>
                <th className="col-received-at">접수 일시</th>
                <th className="col-proposer">민원인</th>
                <th className="col-installation-company">시공사</th>
                <th className="col-issue-description">민원 내용</th>
                <th className="col-received-by">작성자</th>
                <th className="col-receipt-status">
                  처리
                  <br />
                  상태
                </th>
                <th className="col-processed-at">처리 일시</th>
              </tr>
            </thead>
            <tbody>
              {content.length === 0 ? (
                <tr>
                  <td colSpan={8}>데이터가 없습니다.</td>
                </tr>
              ) : (
                content.map((post, index) => (
                  <tr key={post.id}>
                    <td className="col-post-id">{post.id ?? '-'}</td>
                    <td className="col-received-at">
                      <DateTimeCell value={post.receivedAt} />
                    </td>
                    <td className="col-proposer" title={post.proposer || undefined}>
                      <span className="cell-center-wrap">
                        <Link to={`/posts/${post.id}`}>{post.proposer}</Link>
                      </span>
                    </td>
                    <td className="col-installation-company" title={post.installationCompany || undefined}>
                      <span className="cell-center-wrap">{post.installationCompany || '-'}</span>
                    </td>
                    <td className="col-issue-description">
                      <span className="issue-description-clamp" title={post.issueDescription || undefined}>
                        {post.issueDescription || '-'}
                      </span>
                    </td>
                    <td className="col-received-by" title={post.receivedBy || undefined}>
                      {post.receivedBy || '-'}
                    </td>
                    <td className="col-receipt-status">
                      {STATUS_LABEL_MAP[post.receiptStatus] ?? post.receiptStatus}
                    </td>
                    <td className="col-processed-at">
                      <DateTimeCell value={post.processedAt} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={data?.totalPages ?? 1}
        onPageChange={onMovePage}
      />
    </section>
  );
}

export default PostListPage;
